import {
  createContext,
  useMemo,
  useState,
  useCallback,
  useContext,
  useEffect,
} from "react";
import { useParams, useNavigate } from "react-router-dom";

import { DATE_TIME_CONFIG } from "../../config";

import { getJobsByWeek, getScopesByWeek } from "../../services/api/dispatch";
import { useCoreContext } from "./CoreContext";

import {
  getWeekOfDates,
  getDateInLocalTZFromDateStr,
  getShiftedDate,
  getDateString,
  getWeekStart,
} from "../../utils/datetime";

/**
 * Stores all data related to weekly operations. This is very common in this app as it
 * deals with tasks and workflows that are often organized on a weekly basis. This
 * context contains weekly date and navigation data, associated operational data (active
 * jobs, scopes of work, etc.), and associated functions to manage this data.
 *
 * @context WeeklyContext
 *
 * @typedef {Object} WeeklyContextType
 * @property {Date} today - Today's date.
 * @property {boolean} isLoading - Whether data is currently being loaded.
 * @property {Date} weekStart - The start date of the current week.
 * @property {Date} weekEnd - The end date of the current week.
 * @property {Array<Date>} dateRange - First and last visible dates of the week strip.
 * @property {Array<Date>} datesOfWeek - Visible dates for the current week.
 * @property {Array} jobs - The jobs for the current week.
 * @property {Array} scopes - The scopes of work for the current week.
 * @property {Function} reloadJobs - Function to reload the jobs for the current week.
 * @property {Function} resetWeekStart - Function to reset the week start to the default week start.
 * @property {Function} goToWeekContaining - Jump to the week that contains a given date.
 * @property {Function} goToNextDay - Function to navigate to the next day.
 * @property {Function} goToPrevDay - Function to navigate to the previous day.
 * @property {Function} goToNextWeek - Function to navigate to the next week.
 * @property {Function} goToPrevWeek - Function to navigate to the previous week.
 * @property {Function} handleWeekendChange - Function to toggle the visibility of weekends in the week view.
 * @property {Function} reloadScopes - Function to reload the scopes for the current week.
 * @property {Function} applyScopePatch - Merge partial fields into a scope in `scopes` by id (after API updates).
 */
export const WeeklyContext = createContext(null);

/**
 * Read WeeklyContext. Throws if used outside a weekly provider.
 *
 * @returns {Object}
 */
export function useWeeklyContext() {
  const context = useContext(WeeklyContext);
  if (!context) {
    throw new Error("useWeeklyContext must be used within a WeeklyProvider");
  }
  return context;
}

/**
 * Weekly date/job/scope data and week navigation for Dispatch (and similar views).
 *
 * @param {Object} [options] Embed/controller options.
 * @param {Date|null} [options.initialDate=null] Optional date used to seed the week when URL sync is disabled.
 * @param {boolean} [options.disableUrlSync=false] When true, week navigation updates local state only.
 * @param {number|null} [options.startOfWeek=null] Optional week-start day override (0=Sun … 6=Sat).
 * @param {boolean} [options.blankUrlUsesWeekStart=false] When true (Dispatch), a
 *   missing URL date writes week start. Hours Log keeps today's date.
 * @returns {Object}
 */
export function useWeeklyCtx(options = {}) {
  const {
    initialDate = null,
    disableUrlSync = false,
    startOfWeek: startOfWeekOverride = null,
    blankUrlUsesWeekStart = false,
  } = options;
  const navigate = useNavigate();
  const { appName, appDomain } = useCoreContext();

  // Loading states
  const [isLoading, setIsLoading] = useState(false);

  // Current Date data
  const today = useMemo(
    () => getDateInLocalTZFromDateStr(getDateString(new Date())),
    []
  );
  const { date: urlDate } = useParams(); // Get the date parameter from the URL
  const resolveStartOfWeek = useCallback(() => {
    if (startOfWeekOverride != null) {
      return startOfWeekOverride;
    }
    return DATE_TIME_CONFIG.START_OF_WEEK;
  }, [startOfWeekOverride]);

  const [date, setDate] = useState(() => {
    if (disableUrlSync && initialDate) {
      return new Date(initialDate);
    }
    if (!disableUrlSync && urlDate) {
      return getDateInLocalTZFromDateStr(urlDate);
    }
    if (initialDate) {
      return new Date(initialDate);
    }
    return today;
  });
  const updateURL = useCallback(
    // Link URL date with date state (as in reqs)
    (newDate) => {
      if (disableUrlSync) {
        return;
      }
      navigate(
        `/${appDomain.current}/${appName.current}/${getDateString(newDate)}`,
        { replace: true }
      );
    },
    [navigate, appName, appDomain, disableUrlSync]
  );

  // Weekly date data
  const [showWeekends, setShowWeekends] = useState(false);
  const [weekStart, setWeekStart] = useState(() =>
    getWeekStart(date, resolveStartOfWeek())
  );

  // Update URL if no date is present in the URL on initial load
  useEffect(() => {
    if (disableUrlSync) {
      return;
    }
    const blankDate = blankUrlUsesWeekStart ? weekStart : today;
    if (!urlDate && appDomain.current && appName.current) {
      updateURL(blankDate);
    }
  }, [
    urlDate,
    updateURL,
    today,
    weekStart,
    appDomain,
    appName,
    disableUrlSync,
    blankUrlUsesWeekStart,
  ]);

  const weekEnd = useMemo(() => getShiftedDate(weekStart, 6), [weekStart]);
  // Sun–Sat week. When weekends are hidden, drop Sunday (0) and Saturday (6).
  const datesOfWeek = useMemo(() => {
    const allWeekDates = getWeekOfDates(weekStart, weekEnd);
    if (showWeekends) {
      return allWeekDates;
    }
    return allWeekDates.filter(
      (day) => day.getDay() !== 0 && day.getDay() !== 6
    );
  }, [weekStart, weekEnd, showWeekends]);
  const dateRange = useMemo(
    () => [datesOfWeek[0], datesOfWeek[datesOfWeek.length - 1]],
    [datesOfWeek]
  );
  const nextWeekStart = getShiftedDate(weekStart, 7);
  const prevWeekStart = getShiftedDate(weekStart, -7);

  // Week manipulation functions
  /**
   * Open the week that contains ``targetDate`` (week start from company week config).
   *
   * @param {Date} targetDate Date used to resolve the week.
   * @returns {void}
   */
  const goToWeekContaining = useCallback(
    (targetDate) => {
      const newWeekStart = getWeekStart(targetDate, resolveStartOfWeek());
      setWeekStart(newWeekStart);
      setDate(targetDate);
      updateURL(newWeekStart);
    },
    [updateURL, resolveStartOfWeek]
  );

  const resetWeekStart = useCallback(() => {
    const newWeekStart = getWeekStart(today, resolveStartOfWeek());
    setWeekStart(newWeekStart);
    updateURL(newWeekStart);
  }, [updateURL, resolveStartOfWeek, today]);

  const goToNextWeek = useCallback(() => {
    setWeekStart((prev) => getShiftedDate(prev, 7));
    setDate(getShiftedDate(date, 7));
    updateURL(nextWeekStart); // Update the URL
  }, [nextWeekStart, updateURL, date]);

  const goToPrevWeek = useCallback(() => {
    setWeekStart((prev) => getShiftedDate(prev, -7));
    setDate(getShiftedDate(date, -7));
    updateURL(prevWeekStart); // Update the URL
  }, [prevWeekStart, updateURL, date]);

  const handleWeekendChange = useCallback(() => {
    setShowWeekends((prev) => !prev);
  }, []);

  // Day manipulation functions
  const updateWeekIfNeeded = useCallback(
    (newDate) => {
      if (newDate < weekStart || newDate > weekEnd) {
        setWeekStart(getWeekStart(newDate, resolveStartOfWeek()));
      }
    },
    [weekStart, weekEnd, resolveStartOfWeek]
  );

  const goToNextDay = useCallback(() => {
    const newDate = getShiftedDate(date, 1);
    setDate(newDate);
    updateURL(newDate);
    updateWeekIfNeeded(newDate);
  }, [date, updateURL, updateWeekIfNeeded]);

  const goToPrevDay = useCallback(() => {
    const newDate = getShiftedDate(date, -1);
    setDate(newDate);
    updateURL(newDate);
    updateWeekIfNeeded(newDate);
  }, [date, updateURL, updateWeekIfNeeded]);

  // Weekly Job data (actual data of the jobs in this week only)
  const [jobs, setJobs] = useState(null);

  // API call to get the jobs data
  useMemo(() => {
    setIsLoading(true);
    getJobsByWeek(weekStart)
      .then(setJobs)
      .catch((e) =>
        console.error(`could not get jobs for week of ${weekStart}`, e)
      )
      .finally(() => setIsLoading(false));
  }, [weekStart]);

  const reloadJobs = useCallback(async () => {
    setIsLoading(true);
    try {
      const jobs = await getJobsByWeek(weekStart);
      setJobs(jobs);
    } catch (e) {
      console.error(`could not get jobs for week of ${weekStart}`, e);
    } finally {
      setIsLoading(false);
    }
  }, [weekStart]);

  // Scopes data for the week
  const [scopes, setScopes] = useState(null);
  const reloadScopes = useCallback(async () => {
    setIsLoading(true);
    getScopesByWeek(weekStart)
      .then((scopes) => {
        const sortedScopes = scopes.sort((a, b) => a.id - b.id);
        setScopes(sortedScopes);
      })
      .catch((e) =>
        console.error(`could not get scopes for week of ${weekStart}`, e)
      )
      .finally(() => setIsLoading(false));
  }, [weekStart]);
  useMemo(() => {
    reloadScopes();
  }, [reloadScopes]);

  const applyScopePatch = useCallback((scopeId, patch) => {
    const key = String(scopeId);
    setScopes((prev) => {
      if (!prev) return prev;
      return prev.map((s) =>
        String(s.id) === key ? { ...s, ...patch } : s
      );
    });
  }, []);

  const contextValue = useMemo(
    // Memo so if another component calls it doesn't re-run context unless changed
    () => ({
      date,
      today,
      isLoading,
      weekStart,
      weekEnd,
      dateRange,
      datesOfWeek,
      jobs,
      scopes,
      showWeekends,
      setShowWeekends,
      setDate,
      reloadJobs,
      reloadScopes,
      applyScopePatch,
      resetWeekStart,
      goToWeekContaining,
      goToNextDay,
      goToPrevDay,
      goToNextWeek,
      goToPrevWeek,
      handleWeekendChange,
    }),
    [
      date,
      today,
      isLoading,
      weekStart,
      weekEnd,
      dateRange,
      datesOfWeek,
      jobs,
      scopes,
      showWeekends,
      setShowWeekends,
      setDate,
      reloadJobs,
      reloadScopes,
      applyScopePatch,
      resetWeekStart,
      goToWeekContaining,
      goToNextDay,
      goToPrevDay,
      goToNextWeek,
      goToPrevWeek,
      handleWeekendChange,
    ]
  );

  return {
    ...contextValue,
  };
}

export default useWeeklyCtx; // Export the hook as default for backward compatibility
